import { HStack, Section, VStack } from 'tio-ui/components/layout';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { array, hash } from '@ember/helper';
import { getConfigForTasField } from 'tio-common/utils/tuition-assistance/fields';
import Component from '@glimmer/component';
import NavTabs from 'tio-reimagined/components/nav-tabs';
import VerticalProgressTracker from 'tio-common/components/tio/vertical-progress-tracker';
import DescriptionList from 'tio-ui/components/description-list';
import TioFaqs from 'tio-common/components/tio/faqs';
import ProgramReadonlyField from 'tio-common/components/tuition-assistance/program-readonly-field';
import ProgramCustomReadonlyField from 'tio-common/components/tuition-assistance/program-custom-readonly-field';
import type ProgressBuilder from 'tio-common/services/progress-builder';
import type TASProgramInstanceModel from 'tio-common/models/tas-program-instance';
import type { Step } from 'tio-common/components/tio/progress-tracker';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import safeFormatFieldValue from 'tio-common/helpers/safe-format-field-value';
import type TasParticipantModel from 'tio-common/models/tas-participant';
import UploadAttachments from 'tio-common/components/tuition-assistance/forms/upload-attachments';
import type Owner from '@ember/owner';

interface RouteSignature {
  Args: {
    model: {
      programInstance: TASProgramInstanceModel;
      tasParticipant: TasParticipantModel;
    };
  };
}

export default class AuthenticatedTasProgramsDetails extends Component<RouteSignature> {
  @service declare progressBuilder: ProgressBuilder;
  @tracked isOpen = true;
  programAttachments: TASProgramInstanceModel['tasAssets'] = [];

  constructor(owner: Owner, args: RouteSignature['Args']) {
    super(owner, args);
    this.programAttachments = [...args.model.programInstance.tasAssets];
  }

  get programProgress() {
    return this.progressBuilder.programProgress(this.args.model.programInstance) as Step[];
  }

  get programType() {
    let field = getConfigForTasField(
      'PROGRAM_TYPE',
      this.args.model.programInstance.tasProgramTemplate.fields
    );
    return {
      label: field?.label || '',
      value: field?.values[0] || '',
    } as Record<string, string>;
  }

  get employeeCustomFields() {
    const customFields = this.args.model.programInstance.customFields;
    let filtered = customFields.filter(
      (field: { province: string }) => field.province === 'EMPLOYEE'
    );
    return filtered;
  }

  get programCustomFields() {
    const customFields = this.args.model.programInstance.customFields;
    return customFields.filter((field: { province: string }) => field.province === 'PROGRAM');
  }

  get disableUploadAttachments() {
    return ['TAS.ProgramInstanceState.DRAFT', 'TAS.ProgramInstanceState.ABANDONED'].includes(
      this.args.model.programInstance.state
    );
  }

  <template>
    <VStack>
      <NavTabs
        @tabs={{array
          (hash label=(t "tas.program.program") route="authenticated.tas.programs.show")
          (hash label=(t "tas.program.details") route="authenticated.tas.programs.details")
          (hash label=(t "tas.program.history") route="authenticated.tas.programs.history")
        }}
      />
      <HStack>
        <VStack>
          <Section>
            <:header>
              <h1>{{t "tas.program.education_assistance"}}</h1>
            </:header>
            <:body>
              <VStack @collapsed={{true}}>
                <Section>
                  <:body>
                    <h2 class="font-medium">{{t "partner.employee.employee_details"}}</h2>
                    <DescriptionList @striped={{true}} as |l|>
                      <ProgramReadonlyField
                        @name="APPLICATION_ID"
                        @programTemplate={{@model.programInstance.tasProgramTemplate}}
                        @fields={{@model.programInstance.fields}}
                        as |field|
                      >
                        {{#if @model.programInstance.tasProgramTemplate.isDependentProgram}}
                          <l.Group>
                            <l.Term>{{t "tas.applications.dependent"}}</l.Term>
                            <l.Details>{{@model.programInstance.dependentName}}</l.Details>
                          </l.Group>
                        {{/if}}
                        <l.Group>
                          <l.Term>{{field.programLabel}}</l.Term>
                          <l.Details>{{safeFormatFieldValue field.value}}</l.Details>
                        </l.Group>
                      </ProgramReadonlyField>
                      <ProgramReadonlyField
                        @name="NAME"
                        @programTemplate={{@model.programInstance.tasProgramTemplate}}
                        @fields={{@model.programInstance.fields}}
                        as |field|
                      >
                        <l.Group>
                          <l.Term>{{field.programLabel}}</l.Term>
                          <l.Details>{{safeFormatFieldValue field.value}}</l.Details>
                        </l.Group>
                      </ProgramReadonlyField>
                      <ProgramReadonlyField
                        @name="EMPLOYEE_ID"
                        @programTemplate={{@model.programInstance.tasProgramTemplate}}
                        @fields={{@model.programInstance.fields}}
                        as |field|
                      >
                        <l.Group>
                          <l.Term>{{field.programLabel}}</l.Term>
                          <l.Details>{{safeFormatFieldValue field.value}}</l.Details>
                        </l.Group>
                      </ProgramReadonlyField>
                      <ProgramReadonlyField
                        @name="FT_PT_OTHER"
                        @programTemplate={{@model.programInstance.tasProgramTemplate}}
                        @fields={{@model.programInstance.fields}}
                        as |field|
                      >
                        <l.Group>
                          <l.Term>{{field.programLabel}}</l.Term>
                          <l.Details>{{safeFormatFieldValue field.value}}</l.Details>
                        </l.Group>
                      </ProgramReadonlyField>
                      <ProgramReadonlyField
                        @name="EMPLOYEE_EMAIL"
                        @programTemplate={{@model.programInstance.tasProgramTemplate}}
                        @fields={{@model.programInstance.fields}}
                        as |field|
                      >
                        <l.Group>
                          <l.Term>{{field.programLabel}}</l.Term>
                          <l.Details>{{safeFormatFieldValue field.value}}</l.Details>
                        </l.Group>
                      </ProgramReadonlyField>
                      <ProgramReadonlyField
                        @name="HIRE_DATE"
                        @programTemplate={{@model.programInstance.tasProgramTemplate}}
                        @fields={{@model.programInstance.fields}}
                        as |field|
                      >
                        <l.Group>
                          <l.Term>{{field.programLabel}}</l.Term>
                          <l.Details>{{safeFormatDate field.value}}</l.Details>
                        </l.Group>
                      </ProgramReadonlyField>
                      <ProgramReadonlyField
                        @name="EMPLOYEE_DOB"
                        @programTemplate={{@model.programInstance.tasProgramTemplate}}
                        @fields={{@model.programInstance.fields}}
                        as |field|
                      >
                        <l.Group>
                          <l.Term>{{field.programLabel}}</l.Term>
                          <l.Details>{{safeFormatDate field.value}}</l.Details>
                        </l.Group>
                      </ProgramReadonlyField>
                      <ProgramReadonlyField
                        @name="STUDENT_ID"
                        @programTemplate={{@model.programInstance.tasProgramTemplate}}
                        @fields={{@model.programInstance.fields}}
                        as |field|
                      >
                        <l.Group>
                          <l.Term>{{field.programLabel}}</l.Term>
                          <l.Details>{{safeFormatFieldValue field.value}}</l.Details>
                        </l.Group>
                      </ProgramReadonlyField>
                      {{#each this.employeeCustomFields as |field|}}
                        <ProgramCustomReadonlyField @customField={{field}} as |field|>
                          <l.Group>
                            <l.Term>{{field.programLabel}}</l.Term>
                            <l.Details>{{safeFormatFieldValue field.value}}</l.Details>
                          </l.Group>
                        </ProgramCustomReadonlyField>
                      {{/each}}
                    </DescriptionList>

                    <h2 class="mt-6 font-medium">{{t "tas.program.program_approval_details"}}</h2>
                    <DescriptionList @striped={{true}} as |l|>
                      <ProgramReadonlyField
                        @name="SCHOOL_INSTITUTION_NAME"
                        @programTemplate={{@model.programInstance.tasProgramTemplate}}
                        @fields={{@model.programInstance.fields}}
                        as |field|
                      >
                        <l.Group>
                          <l.Term>{{field.programLabel}}</l.Term>
                          <l.Details>{{safeFormatFieldValue field.value}}</l.Details>
                        </l.Group>
                      </ProgramReadonlyField>
                      <ProgramReadonlyField
                        @name="PROGRAM_MAJOR"
                        @programTemplate={{@model.programInstance.tasProgramTemplate}}
                        @fields={{@model.programInstance.fields}}
                        as |field|
                      >
                        <l.Group>
                          <l.Term>{{field.programLabel}}</l.Term>
                          <l.Details>{{safeFormatFieldValue field.value}}</l.Details>
                        </l.Group>
                      </ProgramReadonlyField>
                      <ProgramReadonlyField
                        @name="PROGRAM_SPECIALIZATION"
                        @programTemplate={{@model.programInstance.tasProgramTemplate}}
                        @fields={{@model.programInstance.fields}}
                        as |field|
                      >
                        <l.Group>
                          <l.Term>{{field.programLabel}}</l.Term>
                          <l.Details>{{safeFormatFieldValue field.value}}</l.Details>
                        </l.Group>
                      </ProgramReadonlyField>
                      <ProgramReadonlyField
                        @name="ESTIMATED_PROGRAM_BEGIN"
                        @programTemplate={{@model.programInstance.tasProgramTemplate}}
                        @fields={{@model.programInstance.fields}}
                        as |field|
                      >
                        <l.Group>
                          <l.Term>{{field.programLabel}}</l.Term>
                          <l.Details>{{safeFormatDate field.value}}</l.Details>
                        </l.Group>
                      </ProgramReadonlyField>
                      <ProgramReadonlyField
                        @name="ESTIMATED_PROGRAM_COMPLETION"
                        @programTemplate={{@model.programInstance.tasProgramTemplate}}
                        @fields={{@model.programInstance.fields}}
                        as |field|
                      >
                        <l.Group>
                          <l.Term>{{field.programLabel}}</l.Term>
                          <l.Details>{{safeFormatDate field.value}}</l.Details>
                        </l.Group>
                      </ProgramReadonlyField>
                      <ProgramReadonlyField
                        @name="ENROLLMENT_OBJECTIVE"
                        @programTemplate={{@model.programInstance.tasProgramTemplate}}
                        @fields={{@model.programInstance.fields}}
                        as |field|
                      >
                        <l.Group>
                          <l.Term>{{field.programLabel}}</l.Term>
                          <l.Details>{{safeFormatFieldValue field.value}}</l.Details>
                        </l.Group>
                      </ProgramReadonlyField>
                      {{#each this.programCustomFields as |field|}}
                        <ProgramCustomReadonlyField @customField={{field}} as |field|>
                          <l.Group>
                            <l.Term>{{field.programLabel}}</l.Term>
                            <l.Details>{{safeFormatFieldValue field.value}}</l.Details>
                          </l.Group>
                        </ProgramCustomReadonlyField>
                      {{/each}}
                    </DescriptionList>
                  </:body>
                </Section>
                {{#if @model.programInstance.tasProgramTemplate.programFaqs}}
                  <Section>
                    <h2 class="font-medium">
                      {{t "common.frequently_asked_questions"}}
                    </h2>
                    {{#each @model.programInstance.tasProgramTemplate.programFaqs as |faq|}}
                      <TioFaqs @question={{faq.heading}} @answer={{faq.body}} class="mt-3" />
                    {{/each}}
                  </Section>
                {{/if}}
              </VStack>
            </:body>
          </Section>
          <Section>
            <:header>
              <div>{{t "common.attachments"}}</div>
            </:header>
            <:body>
              <UploadAttachments
                @programTemplate={{@model.programInstance.tasProgramTemplate}}
                @classification="COMPLETION_DOCUMENTS"
                @attachable={{@model.programInstance}}
                @attachableType="tasProgramInstance"
                @useDropzone={{true}}
                @hideInstructions={{false}}
                @disabled={{false}}
                @hideDiscarded={{true}}
              />
            </:body>
          </Section>
        </VStack>

        <Section class="md:w-1/3!">
          <:header>{{t "common.progress_tracker.program_progress"}}</:header>
          <:body>
            {{! TODO: using lists to show numbers causes flow issues. The ml-4 class will force
                the numbers into the box, but this isn't ideal as mobile view will have issues.
                Looks like the component is just a stack anyway so might be target for update. }}
            <VerticalProgressTracker class="ml-4" @steps={{this.programProgress}} />
          </:body>
        </Section>
      </HStack>
    </VStack>
  </template>
}
